import { useLingui } from '@lingui/react/macro';
import { useRef, useState, type ReactNode } from 'react';

import { useIsMobile } from '@/shared/mobile';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '@/shared/ui/drawer';

export interface ConfirmOptions {
  /** Pergunta com o nome do registro: "Remover Juliana Prado Menezes?". */
  readonly title: ReactNode;
  /** O que vai acontecer, em uma frase (U39 §6). */
  readonly description?: ReactNode;
  /** Verbo da ação no botão: "Sim, remover" (U20-6). */
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  readonly tone?: 'destructive' | 'default';
}

export interface ConfirmDialogProps extends ConfirmOptions {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Pode ser assíncrono: o botão mostra progresso e o diálogo só fecha quando termina. */
  readonly onConfirm: () => unknown;
}

/**
 * Confirmação local (P09): remover item de uma lista do formulário, descartar um rascunho. Ação com
 * efeito no servidor passa pelo `ActionDialog` de `shared/action`. Diálogo na web, bottom sheet no
 * iPhone.
 */
export function ConfirmDialog({ open, onOpenChange, onConfirm, title, description, confirmLabel, cancelLabel, tone = 'destructive' }: ConfirmDialogProps) {
  const { t } = useLingui();
  const isMobile = useIsMobile();
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };
  const change = (next: boolean) => {
    if (!busy) onOpenChange(next);
  };
  const cancel = cancelLabel ?? t`Não, cancelar`;
  const action = (
    <Button variant={tone === 'destructive' ? 'danger-solid' : 'primary'} size={isMobile ? 'lg' : 'default'} loading={busy} onClick={() => void confirm()}>
      {confirmLabel}
    </Button>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={change} dismissible={!busy}>
        <DrawerContent data-slot="confirm-dialog" role="alertdialog">
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{description ?? t`Confira antes de continuar.`}</DrawerDescription>
          </DrawerHeader>
          <DrawerFooter>
            {action}
            <Button variant="secondary" size="lg" disabled={busy} onClick={() => change(false)}>
              {cancel}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={change}>
      <AlertDialogContent data-slot="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description ?? t`Confira antes de continuar.`}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{cancel}</AlertDialogCancel>
          {action}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface Pending {
  readonly options: ConfirmOptions;
  readonly resolve: (value: boolean) => void;
}

/**
 * Confirmação como promessa: `if (await confirm({ title, confirmLabel: t\`Sim, remover\` })) remove()`.
 * Ponha o `element` uma vez no componente.
 */
export function useConfirm(): readonly [(options: ConfirmOptions) => Promise<boolean>, ReactNode] {
  const [pending, setPending] = useState<Pending | null>(null);
  const [open, setOpen] = useState(false);
  const answered = useRef(false);
  const confirm = (options: ConfirmOptions) =>
    new Promise<boolean>((resolve) => {
      answered.current = false;
      setPending({ options, resolve });
      setOpen(true);
    });
  const element = pending ? (
    <ConfirmDialog
      {...pending.options}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next && !answered.current) {
          answered.current = true;
          pending.resolve(false);
        }
      }}
      onConfirm={() => {
        answered.current = true;
        pending.resolve(true);
      }}
    />
  ) : null;
  return [confirm, element] as const;
}
